import React, { useEffect, useState } from "react";

import { API } from "aws-amplify";
import {
  syncableAccountCreateProps,
  syncableAccountUpdateProps,
} from "../../../graphql/account/accountCRUD";

import {
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  DialogContentText,
} from "@material-ui/core";

import { Confirm, useListContext, useNotify, useRefresh } from "react-admin";

import { ExportMenuItem } from "../../../components/common/ExportButton";
import {
  ExportIcon,
  ImportExportIcon,
  ImportIcon,
  UploadIcon,
} from "../../../components/common/iconography";

import { logError, logHistory } from "../../../utils/appUtils";
import {
  hasExternalId,
  fetchTriggerEnabled,
  sanitizeZapierData,
  triggerEvent,
} from "../../../utils/zapierUtils";
import {
  ExternalStatus,
  MAX_INVENTORY_QUERY_LIMIT,
  ResourceType,
} from "../../../utils/constants";

import {
  createAccountFromCustomer,
  fetchAccounts,
  normalizeWebsiteUrl,
  prepareImportedCustomer,
  setAccountSyncFailed,
  setAccountSyncPending,
  updateAccountFromCustomer,
} from "./AccountUtils";
import { AccountImportDialog } from "./AccountImportDialog";

const AccountSyncMenuButton = ({
  exporter,
  disabled: disabledProp,
  ...props
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isUploadEnabled, setIsUploadEnabled] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const open = Boolean(anchorEl);
  const { total } = useListContext();
  const perPage = MAX_INVENTORY_QUERY_LIMIT - 51; // fixed perPage to avoid querySignature duplicates

  useEffect(() => {
    const checkTrigger = async () => {
      try {
        const triggerEnabled = await fetchTriggerEnabled("accountCreated");
        setIsUploadEnabled(triggerEnabled);
      } catch (err) {
        logError("accounts:SyncMenuButton:triggerEnabled", err);
        setIsUploadEnabled(false);
      }
    };
    checkTrigger();
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleImport = () => {
    setImportOpen(true);
    handleClose();
  };

  // CSV import (via AccountImportDialog): persist rows in AppSync—new accounts via
  // `createAccountFromCustomer`, matched rows via `updateAccountFromCustomer`—then
  // fire Zapier `accountImported` so external workflows can link customers / set external IDs.
  const processImport = async (importedCustomers) => {
    debugger;
    try {
      setLoading(true);
      // do not sync already synced accounts
      const filteredCustomers = importedCustomers.filter(
        (c) => !hasExternalId(c),
      );

      let allAccounts = [];
      for (const rawCustomer of filteredCustomers) {
        const customer = prepareImportedCustomer(rawCustomer);
        let accountToSync = null;
        if (!customer.reloadYardId) {
          // Create missing accounts & log history
          const accountCreated = await createAccountFromCustomer(
            customer,
            ExternalStatus.SYNC_PENDING,
          );
          accountToSync = sanitizeZapierData(
            syncableAccountCreateProps,
            accountCreated,
          );
          allAccounts.push(accountToSync);
        } else {
          // account matched
          const accountUpdated = await updateAccountFromCustomer(customer);

          accountToSync = sanitizeZapierData(
            syncableAccountUpdateProps,
            accountUpdated,
          );
          allAccounts.push(accountToSync);
        }
      }
      // Trigger Zapier event for all customers
      // retrievee externalId and update each imported account record.
      allAccounts = allAccounts.map((acc) => ({
        ...acc,
        website: normalizeWebsiteUrl(acc?.website),
      }));
      await API.post("zapierREST", "/handleTrigger", {
        body: {
          event: "accountImported",
          quaries: allAccounts,
          optionsOverride: {},
          reloadYardId: window.appUser.reloadYardId,
        },
      });

      notify && notify("Import request submitted.", "info");
    } catch (err) {
      logError("accounts:SuncMenuButton:processImport", err, notify);
    } finally {
      setLoading(false);
      refresh();
    }
  };

  const handleUploadOpen = () => {
    setUploadOpen(true);
  };
  const handleUploadClose = () => {
    setUploadOpen(false);
    handleClose();
  };
  // Upload only active accounts that are not synced yet.
  // Create a new customer record in the external system.
  const processUpload = async () => {
    debugger;
    try {
      setLoading(true);
      let allAccounts = await fetchAccounts(notify);
      allAccounts = allAccounts.filter(
        (acc) => acc.active === "true" && !hasExternalId(acc),
      );

      // for import accounts from external systems.
      for (const account of allAccounts) {
        try {
          const sanitizedData = sanitizeZapierData(
            syncableAccountCreateProps,
            account,
          );
          const normalizedData = {
            ...sanitizedData,
            website: normalizeWebsiteUrl(sanitizedData.website),
          };
          triggerEvent(normalizedData, "accountCreated")
            .then(() => {
              setAccountSyncPending(account);
              const historyNote = {
                parentId: account.id,
                parentType: ResourceType.ACCOUNT,
                text: "Account Uploaded.",
              };
              logHistory(historyNote);
            })
            .catch((error) => {
              logError("accounts:SyncMenuButton:triggerEvent", error);
              setAccountSyncFailed(account);
            });
        } catch (error) {
          setAccountSyncFailed(account);
          logError("accounts:SyncMenuButton:processUpload", error, notify);
        }
      }
      notify && notify("Upload request Submitted.", "info");
    } catch (error) {
      logError("accounts:SyncMenuButton:processUpload", error, notify);
    } finally {
      setLoading(false);
      setUploadOpen(false);
      handleClose();
    }
  };

  return (
    <>
      <Button
        id="sync-button"
        aria-controls={open ? "sync-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        color="primary"
        size="small"
        disabled={disabledProp || total === 0}
        startIcon={
          loading ? (
            <CircularProgress color="inherit" size={17} />
          ) : (
            <ImportExportIcon />
          )
        }
      >
        Sync
      </Button>

      <Menu
        id="sync-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        getContentAnchorEl={null}
      >
        <MenuItem onClick={handleImport}>
          <ListItemIcon>
            <ImportIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="IMPORT" />
        </MenuItem>
        <MenuItem onClick={handleUploadOpen} disabled={!isUploadEnabled}>
          <ListItemIcon>
            <UploadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="UPLOAD" />
        </MenuItem>

        <ExportMenuItem
          label="EXPORT"
          icon={<ExportIcon fontSize="small" />}
          exporter={exporter}
          perPage={perPage}
        />
      </Menu>
      {importOpen && (
        <AccountImportDialog
          total={total}
          isOpen={importOpen}
          setIsOpen={setImportOpen}
          handleImport={processImport}
        />
      )}
      <Confirm
        isOpen={uploadOpen}
        loading={loading}
        title="Upload Confirmation"
        content={
          <DialogContentText>
            This will update all customer records in your accounting system.
            <br />
            Are you sure you want to proceed?
          </DialogContentText>
        }
        onConfirm={processUpload}
        onClose={handleUploadClose}
      />
    </>
  );
};

export default AccountSyncMenuButton;
